import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Toggle from 'react-toggle';
import { CSVLink } from 'react-csv';
import { LineChart } from '../LineChart';
import { ConnectedMultiLineChart } from '../MultiLineChart';
import { ConnectedDatePickerWithRouter } from '../DatePicker';
import { ConnectedSelectFilterWithRouter } from '../SelectFilter';
import { ConnectedSelectSuiteWithRouter } from '../SelectSuite';
import { Spinner } from '../Spinner';
import { DiagnosticsActions } from '../../store';
import './MainDisplayBox.css';

// const printIcon = require('./assets/print-icon.png');
const downloadIcon = require('./assets/download-icon.png');

export class MainDisplayBox extends Component {
  state = { datePickerIsSelected: false, CSVData: '' };
  csvLink = React.createRef();
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchCSVData = async () => {
    const { getCSVData } = this.props;

    const CSVData = await getCSVData();

    if (CSVData) {
      this.setState(
        (state) => ({
          ...state,
          CSVData,
        }),
        () => {
          this.csvLink.current.link.click();
        },
      );
    }
  };

  handleCustomClick = () => {
    this.setState((state) => ({ ...state, datePickerIsSelected: true }));
  };

  handleClose = () => {
    if (this._isMounted) {
      this.setState((state) => ({ ...state, datePickerIsSelected: false }));
    }
  };

  renderTitle() {
    const {
      selectedBuilding: { name: buildingName },
      navigationParams: {
        selectedCategory: { name: categoryName },
        selectedSubCategory: { name: subcategoryName },
      },
    } = this.props;

    switch (categoryName) {
      case 'Thermostat':
        return <ConnectedSelectSuiteWithRouter className="main-display-box-header__title" />;
      default:
        return (
          <React.Fragment>
            <span className="main-display-box-header__title main-display-box-header__title--desktop">
              {`${buildingName}`}
            </span>
            <span className="main-display-box-header__title--mobile">{subcategoryName}</span>
            <span className="main-display-box-header__title">{`${categoryName}: ${subcategoryName}`}</span>
          </React.Fragment>
        );
    }
  }

  renderHeader() {
    const {
      weatherAdjusted,
      toggleWeatherAdjusted,
      navigationParams: {
        selectedCategory: { name: categoryName },
      },
    } = this.props;

    return (
      <React.Fragment>
        <div
          className={
            categoryName === 'Thermostat'
              ? 'main-display-box-header main-display-box-header__thermostat'
              : 'main-display-box-header'
          }
        >
          {this.renderTitle()}
          <span
            className={
              categoryName === 'Thermostat'
                ? 'main-display-box-header-menu-mobile main-display-box-header-menu-mobile__thermostat'
                : 'main-display-box-header-menu-mobile'
            }
          >
            {categoryName === 'Thermostat' && <span className="date-select-title__thermostat">Select Date Range</span>}
            {this.state.datePickerIsSelected ? (
              <ConnectedDatePickerWithRouter onClose={this.handleClose} />
            ) : (
              <ConnectedSelectFilterWithRouter onCustomClick={this.handleCustomClick} />
            )}
          </span>
          <span className="main-display-box-header-menu-desktop">
            {this.state.datePickerIsSelected ? (
              <ConnectedDatePickerWithRouter onClose={this.handleClose} />
            ) : (
              <ConnectedSelectFilterWithRouter onCustomClick={this.handleCustomClick} />
            )}
            {categoryName === 'Diagnostics' && (
              <div className="main-display-box-header-menu__button">
                <button className="main-display-box__download-button" onClick={this.fetchCSVData}>
                  <img id="download-button" src={downloadIcon} alt="" height="20" width="22" />
                </button>

                <CSVLink
                  data={this.state.CSVData}
                  filename="data.csv"
                  className="hidden"
                  ref={this.csvLink}
                  target="_blank"
                />
              </div>
            )}
          </span>
        </div>
        {(categoryName === 'Gas Usage' || categoryName === 'Steam Usage') && (
          <div className="main-display-box-header-toggle">
            <span className="main-display-box-header__title--mobile space-right">Weather Adjusted</span>
            <span className="main-display-box-header__title space-right">Weather Adjusted</span>
            <Toggle defaultChecked={weatherAdjusted} icons={false} onChange={toggleWeatherAdjusted} />
          </div>
        )}
      </React.Fragment>
    );
  }

  renderChart() {
    const {
      isLoading,
      graphData = [],
      unit,
      reportUnit,
      navigationParams: {
        selectedCategory: { name: categoryName },
      },
    } = this.props;

    if (isLoading) {
      return (
        <div className="main-display-box main-display-box--loading">
          <Spinner />
        </div>
      );
    }

    if (categoryName === 'Diagnostics' || categoryName === 'Thermostat') {
      return <ConnectedMultiLineChart name={categoryName} />;
    }

    return <LineChart dataPoints={graphData} unit={unit} reportUnit={reportUnit} />;
  }

  render() {
    const {
      isLoading,
      diagnosticsLineColorObj,
      navigationParams: {
        selectedCategory: { name: categoryName },
      },
    } = this.props;
    if (isLoading) {
      return (
        <div className="main-display-box main-display-box--loading">
          <Spinner />
        </div>
      );
    }

    if (categoryName === 'Diagnostics') {
      if (!diagnosticsLineColorObj) {
        return (
          <div className="main-display-box main-display-box--loading">
            <Spinner />
          </div>
        );
      }
    }

    return (
      <div className="main-display-box">
        {this.renderHeader()}
        {this.renderChart()}
      </div>
    );
  }
}

MainDisplayBox.propTypes = {
  date: PropTypes.string,
  graphData: PropTypes.arrayOf(
    PropTypes.shape({
      actual: PropTypes.number,
      baseline: PropTypes.number,
      target: PropTypes.number,
      x: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool.isRequired,
  diagnosticsLineColorObj: PropTypes.shape({}),
  suiteLineColorObj: PropTypes.shape({}),
  reportUnit: PropTypes.string,
  categoryName: PropTypes.string,
  subcategoryName: PropTypes.string,
  unit: PropTypes.string,
  getCSVData: PropTypes.func,
  weatherAdjusted: PropTypes.bool,
  toggleWeatherAdjusted: PropTypes.func,
};

MainDisplayBox.defaultProps = {
  unit: '',
  date: '',
  graphData: undefined,
  reportUnit: undefined,
  subcategoryName: '',
  diagnosticsLineColorObj: {},
  suiteLineColorObj: {},
  getCSVData: undefined,
};

function mapStateToProps(state) {
  return {
    diagnosticsLineColorObj: state.diagnosticsReducer.lineColorObj,
    suiteLineColorObj: state.suiteReducer.suiteLineColors,
    diagnosticsData: state.diagnosticsReducer.diagnosticsData,
    selectedBuilding: state.buildingReducer.selectedBuilding,
    navigationParams: state.buildingReducer.navigationParams,
  };
}

const mapDispatchToProps = {
  getCSVData: DiagnosticsActions.getCSVData,
};

const ConnectedMainDisplayBox = connect(mapStateToProps, mapDispatchToProps)(MainDisplayBox);
export const ConnectedMainDisplayBoxWithRouter = withRouter(ConnectedMainDisplayBox);
